// src/components/EmployeeBookingAlert.jsx
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import chatWS from "../services/chatWebSocket";
import bookingWS from "../services/bookingWebSocket"; // Import bookingWS
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EmployeeBookingAlert = () => {
  const navigate = useNavigate();
  const { userDetails, authKey } = useSelector((state) => state.auth);
  const audioContextRef = useRef(null);

  // Connect to Chat WS AND Booking WS
  useEffect(() => {
    if (!userDetails || !authKey) return;

    const role = userDetails.role?.toLowerCase() || "";
    // Only connect if user is NOT a driver AND NOT an Admin
    // Drivers have DriverDashboard. Admins have AdminEmergencyAlert.
    const isDriver = role === "driver" || userDetails.role === "D";
    const isAdmin = role === "admin" || role === "ad";
    
    console.log("[EmployeeBookingAlert] Checks - isDriver:", isDriver, "isAdmin:", isAdmin);
    
    // Allow Supervisors, Engineers, Employees
    if (authKey && userDetails.ServiceNo && !isDriver && !isAdmin) {
      console.log("[EmployeeBookingAlert] ✅ Connecting as Employee/Supervisor");
      
      // 1. Connect as Employee to Chat WS
      chatWS.connect(authKey, userDetails.ServiceNo, "Employee");

      const unsubscribeChat = chatWS.onMessage((msg) => {
        console.log("[EmployeeBookingAlert] 📨 Received from ChatWS:", msg);
        processMessage(msg);
      });

      // 2. Connect as Employee to Booking WS (if not already connected)
      // This ensures we receive updates even if we left the Booking page
      bookingWS.connectAsEmployee(authKey);
      
      const unsubscribeBooking = bookingWS.onStatusUpdate((msg) => {
        console.log("[EmployeeBookingAlert] 📨 Received from BookingWS:", msg);
        processMessage(msg);
      });

      return () => {
        if (unsubscribeBooking) unsubscribeBooking();
        if (unsubscribeChat) unsubscribeChat();
        // Do NOT disconnect chatWS here, as AdminDashboard might be using it
      };
    }
  }, [authKey, userDetails]);

  const processMessage = (msg) => {
    console.log("[EmployeeBookingAlert] 🔍 Processing message:", msg);
    
    // Check if message is a status update for a booking
    // MUST have a booking ID (bId) to be a valid booking alert
    if ((msg.type === "status_update" || msg.type === "booking_status_update" || msg.status) && msg.bId) {
      console.log("[EmployeeBookingAlert] ✅ Has bId and status");
      
      // 🔒 SECURITY CHECK: Ensure this message is meant for THIS employee
      // Different message types have different user identification fields
      
      // 0. Ensure we have a valid ServiceNo to check against
      if (!userDetails?.ServiceNo) {
        console.log("[EmployeeBookingAlert] ❌ Ignoring message - User ServiceNo not available");
        return;
      }
      
      // 1. For chatWS messages (type === "status_update"): Check targetUserId
      if (msg.type === "status_update") {
        if (!msg.targetUserId || msg.targetUserId === "undefined" || msg.targetUserId === "null") {
          console.log("[EmployeeBookingAlert] ⚠️ Ignoring chat message with missing/invalid targetUserId:", msg.targetUserId);
          return;
        }
        if (String(msg.targetUserId) !== String(userDetails?.ServiceNo)) {
          console.log(`[EmployeeBookingAlert] ❌ Ignoring chat message for user ${msg.targetUserId} (I am ${userDetails?.ServiceNo})`);
          return;
        }
      }
      
      // 2. For bookingWS messages (type === "booking_status_update"): Check bookingSno
      else if (msg.type === "booking_status_update") {
        if (!msg.bookingSno || msg.bookingSno === "undefined" || msg.bookingSno === "null") {
          console.log("[EmployeeBookingAlert] ⚠️ Ignoring booking message with missing/invalid bookingSno:", msg.bookingSno);
          return;
        }
        if (String(msg.bookingSno) !== String(userDetails?.ServiceNo)) {
          console.log(`[EmployeeBookingAlert] ❌ Ignoring booking message for user ${msg.bookingSno} (I am ${userDetails?.ServiceNo})`);
          return;
        }
      }
      
      // 3. For other message types: require targetUserId as fallback
      else {
        if (!msg.targetUserId || msg.targetUserId === "undefined" || msg.targetUserId === "null") {
          console.log("[EmployeeBookingAlert] ⚠️ Ignoring message with missing/invalid targetUserId:", msg.targetUserId);
          return;
        }
        if (String(msg.targetUserId) !== String(userDetails?.ServiceNo)) {
          console.log(`[EmployeeBookingAlert] ❌ Ignoring message for user ${msg.targetUserId} (I am ${userDetails?.ServiceNo})`);
          return;
        }
      }

      console.log("[EmployeeBookingAlert] ✅ Message is for THIS employee! Status:", msg.status);

      if (msg.status === "A" || msg.status === "ACCEPTED") {
        handleAlert("accepted", msg);
      } else if (msg.status === "C" || msg.status === "DECLINED" || msg.status === "REJECTED") {
        handleAlert("declined", msg);
      }
    } else {
      console.log("[EmployeeBookingAlert] ⚠️ Message missing bId or status. Has bId:", !!msg.bId, "Has status:", !!msg.status);
    }
  };

  const playSound = (type) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const now = ctx.currentTime;

      if (type === "accepted") {
        // Success Sound (Positive Chime)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = "sine";
        osc.frequency.setValueAtTime(523.25, now); // C5
        osc.frequency.exponentialRampToValueAtTime(1046.5, now + 0.1); // C6
        
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
        
        osc.start(now);
        osc.stop(now + 0.5);

        // Harmony
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        
        osc2.type = "triangle";
        osc2.frequency.setValueAtTime(659.25, now); // E5
        osc2.frequency.exponentialRampToValueAtTime(1318.5, now + 0.1); // E6
        
        gain2.gain.setValueAtTime(0.3, now);
        gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
        
        osc2.start(now);
        osc2.stop(now + 0.5);

      } else {
        // Decline Sound (Negative/Error)
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(440, now); // A4
        osc.frequency.linearRampToValueAtTime(220, now + 0.3); // A3
        
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
        
        osc.start(now);
        osc.stop(now + 0.4);
      }
    } catch (e) {
      console.error("Audio play failed", e);
    }
  };

  // 🔴 NEW: Show employee-style toast with button & sound (global notifications)
  const showEmployeeStyleToast = (type, bId) => {
    // Create audio context for sound
    const playSound = (soundType) => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;

        const ctx = new AudioContext();
        const now = ctx.currentTime;

        if (soundType === "accepted") {
          // Success Sound (Positive Chime)
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = "sine";
          osc.frequency.setValueAtTime(523.25, now); // C5
          osc.frequency.exponentialRampToValueAtTime(1046.5, now + 0.1); // C6

          gain.gain.setValueAtTime(0.3, now);
          gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

          osc.start(now);
          osc.stop(now + 0.5);

          // Harmony
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);

          osc2.type = "triangle";
          osc2.frequency.setValueAtTime(659.25, now); // E5
          osc2.frequency.exponentialRampToValueAtTime(1318.5, now + 0.1); // E6

          gain2.gain.setValueAtTime(0.3, now);
          gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

          osc2.start(now);
          osc2.stop(now + 0.5);

        } else {
          // Decline Sound (Negative/Error)
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.type = "sawtooth";
          osc.frequency.setValueAtTime(440, now); // A4
          osc.frequency.linearRampToValueAtTime(220, now + 0.3); // A3

          gain.gain.setValueAtTime(0.3, now);
          gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);

          osc.start(now);
          osc.stop(now + 0.4);
        }
      } catch (e) {
        console.error("Audio play failed", e);
      }
    };

    // Show toast with button (same as EmployeeBookingAlert)
    const message = type === "accepted"
      ? `Driver accepted hire #${bId}. You can see details on my bookings.`
      : `Driver declined hire #${bId}. Try another one.`;

    toast(
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <strong>{type === "accepted" ? "Booking Accepted!" : "Booking Declined"}</strong>
          <div style={{ fontSize: 12 }}>{message}</div>
        </div>
        <div className="flex gap-2">
          {type === "accepted" ? (
            <button
              onClick={() => {
                toast.dismiss();
                navigate("/employee/my-bookings");
              }}
              className="bg-green-600 text-white px-3 py-1 rounded"
            >
              View Bookings
            </button>
          ) : (
            <button
              onClick={() => {
                toast.dismiss();
                navigate("/employee-dashboard");
              }}
              className="bg-red-600 text-white px-3 py-1 rounded"
            >
              Find Another
            </button>
          )}
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: true,
        position: "top-center",
        style: { minWidth: '400px' }
      }
    );

    // Play sound
    playSound(type);
  };

  const handleAlert = (type, msg) => {
    // Show the same toast approach that worked globally on driver side
    showEmployeeStyleToast(type, msg.bId);
  };

  // This component now only handles WebSocket connections and shows toasts
  // No visible UI - toasts appear via react-toastify
  return null;
};

export default EmployeeBookingAlert;
